/**
 * Where the browser engine lives and whether it is present.
 *
 * The WASM artifact is a GPL-licensed vendored binary (see
 * `docs/operational/license-sbom.md`), not a bundled npm import: `npm run
 * vendor:engine` copies the pinned build into `public/engine/`, and Vite serves it
 * verbatim. Emscripten resolves the sibling `.wasm` relative to the worker script
 * URL, so the two files must stay together under one directory.
 *
 * A deployment that has not vendored the artifact is a supported state, not a bug:
 * `probeEngineAsset` reports it and the UI falls back to the server evaluation path.
 */

/** Directory the vendored engine is served from (see `scripts/vendor-engine.mjs`). */
export const ENGINE_BASE_PATH = '/engine';

/** Pinned build. Bump together with `sbom_artifacts.json` and the vendor script. */
export const ENGINE_BUILD = 'stockfish-19-lite-single';

/** Human-readable name of `ENGINE_BUILD`, shown wherever a local number is. */
export const ENGINE_LABEL = 'Stockfish 19';

export const ENGINE_SCRIPT_URL = `${ENGINE_BASE_PATH}/${ENGINE_BUILD}.js`;

/** Engine hash table, in MiB. Small on purpose: this runs on the user's device. */
export const ENGINE_HASH_MB = 16;

/** Depth the interactive panel searches to; mirrors the server request (§7.5 free tier). */
export const DEFAULT_DEPTH = 18;

/** Lines shown at once. The §4 cache allows up to 3; locally this is display-only. */
export const DEFAULT_MULTI_PV = 1;

export type EngineAvailability =
  | { kind: 'available' }
  | { kind: 'missing' }
  | { kind: 'unsupported'; reason: string };

/**
 * Static capability check, before any network request.
 *
 * The single-threaded build needs neither `SharedArrayBuffer` nor cross-origin
 * isolation — only baseline WebAssembly and Workers, which is why it is the default
 * (ADR-0015).
 */
export function checkEngineSupport(): EngineAvailability {
  if (typeof Worker === 'undefined') {
    return { kind: 'unsupported', reason: 'this browser has no Web Worker support' };
  }
  if (typeof WebAssembly === 'undefined') {
    return { kind: 'unsupported', reason: 'this browser has no WebAssembly support' };
  }
  return { kind: 'available' };
}

/**
 * Confirm the vendored artifact is actually being served.
 *
 * A `HEAD` is used so the check costs nothing: the SPA history fallback in
 * `deploy/docker/frontend.nginx.conf` returns `index.html` (200, `text/html`) for
 * unknown paths, so a 200 alone would be a false positive — the content type is what
 * distinguishes a served script from the shell.
 */
export async function probeEngineAsset(
  fetchImpl: typeof fetch = fetch,
  url: string = ENGINE_SCRIPT_URL,
): Promise<EngineAvailability> {
  const support = checkEngineSupport();
  if (support.kind !== 'available') return support;
  try {
    const response = await fetchImpl(url, { method: 'HEAD' });
    if (!response.ok) return { kind: 'missing' };
    const contentType = response.headers.get('content-type') ?? '';
    if (contentType.includes('text/html')) return { kind: 'missing' };
    return { kind: 'available' };
  } catch {
    return { kind: 'missing' };
  }
}
